import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  MAINTENANCE_TABLE,
  PengaturanMaintenance,
  StatusMaintenance,
  getStatusMaintenance,
  getWaktuPerubahanBerikutnya,
} from '../utils/maintenance';

const CACHE_KEY = 'mias_maintenance_v1';
const INTERVAL_CEK_MS = 60 * 1000;

const bacaCache = (): PengaturanMaintenance | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as PengaturanMaintenance) : null;
  } catch {
    return null;
  }
};

const simpanCache = (p: PengaturanMaintenance | null) => {
  try {
    if (p) localStorage.setItem(CACHE_KEY, JSON.stringify(p));
    else localStorage.removeItem(CACHE_KEY);
  } catch {
    // localStorage penuh atau diblokir, abaikan
  }
};

/**
 * Status Mode Maintenance untuk seluruh aplikasi.
 *
 * Dibaca dari Supabase saat dibuka, lewat Realtime saat diubah, setiap 60 detik,
 * dan saat aplikasi kembali dibuka dari latar belakang. Hasil terakhir disimpan
 * di localStorage, jadi bila Supabase tidak terjangkau dipakai status terakhir.
 * Bila belum pernah berhasil membaca sama sekali, aplikasi tetap dibuka (tidak
 * mengunci pengguna karena gangguan jaringan).
 */
export const useMaintenance = () => {
  const [pengaturan, setPengaturan] = useState<PengaturanMaintenance | null>(() => bacaCache());
  const [siap, setSiap] = useState<boolean>(() => bacaCache() !== null);
  const [now, setNow] = useState(() => Date.now());

  const muat = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from(MAINTENANCE_TABLE)
        .select('*')
        .eq('id', 1)
        .maybeSingle();
      if (error) throw error;
      const hasil = (data as PengaturanMaintenance | null) ?? null;
      setPengaturan(hasil);
      simpanCache(hasil);
    } catch (e) {
      console.warn('[Maintenance] Gagal membaca status, memakai status terakhir:', e);
    } finally {
      setNow(Date.now());
      setSiap(true);
    }
  }, []);

  useEffect(() => {
    muat();
    // Jangan menahan aplikasi terlalu lama bila jaringan lambat.
    const batas = setTimeout(() => setSiap(true), 5000);
    const interval = setInterval(muat, INTERVAL_CEK_MS);
    const saatTerlihat = () => {
      if (document.visibilityState === 'visible') muat();
    };
    document.addEventListener('visibilitychange', saatTerlihat);
    window.addEventListener('online', muat);

    const channel = supabase
      .channel('pengaturan_maintenance')
      .on('postgres_changes', { event: '*', schema: 'public', table: MAINTENANCE_TABLE }, () => muat())
      .subscribe();

    return () => {
      clearTimeout(batas);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', saatTerlihat);
      window.removeEventListener('online', muat);
      supabase.removeChannel(channel);
    };
  }, [muat]);

  // Timer tepat di waktu mulai / selesai otomatis, supaya halaman berganti sendiri.
  useEffect(() => {
    const berikutnya = getWaktuPerubahanBerikutnya(pengaturan, now);
    if (berikutnya === null) return;
    const jeda = Math.min(berikutnya - Date.now() + 500, 2 ** 31 - 1);
    const t = setTimeout(() => setNow(Date.now()), Math.max(jeda, 0));
    return () => clearTimeout(t);
  }, [pengaturan, now]);

  const status: StatusMaintenance = getStatusMaintenance(pengaturan, now);

  return { pengaturan, status, siap, muatUlang: muat };
};
